import { Card, CardContent } from "@/components/ui/card";
import { Building2, Sun, Factory, ArrowRight } from "lucide-react";
import { INDUSTRIES, type Industry } from "@/data/personaPresets";

interface IndustrySelectStepProps {
  onSelect: (industry: Industry) => void;
}

const iconMap: Record<string, React.ElementType> = {
  building: Building2,
  sun: Sun,
  factory: Factory,
};

export function IndustrySelectStep({ onSelect }: IndustrySelectStepProps) {
  return (
    <div className="space-y-10 max-w-3xl mx-auto">
      <div className="text-center space-y-4">
        <span className="inline-block text-xs font-semibold tracking-widest uppercase text-fordje-blue">
          Step 1 — Get Started
        </span>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          What industry are you in?
        </h2>
        <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
          Select your industry so we can tailor the data points and jurisdictions that matter most to your team.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {INDUSTRIES.map((industry) => {
          const IconComponent = iconMap[industry.icon];
          return (
            <Card
              key={industry.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(industry)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(industry);
                }
              }}
              className="group relative cursor-pointer overflow-hidden border border-border/60 bg-card transition-all duration-300 hover:-translate-y-1 hover:border-fordje-blue/60 hover:shadow-[0_20px_60px_-20px_hsl(221_83%_53%/0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fordje-blue"
            >
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-fordje-blue/5 via-transparent to-fordje-accent/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <CardContent className="relative p-7 flex flex-col gap-5">
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-fordje-blue/15 to-fordje-accent/15 flex items-center justify-center ring-1 ring-fordje-blue/20 transition-transform duration-300 group-hover:scale-110">
                    <IconComponent className="h-6 w-6 text-fordje-blue" />
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground transition-all duration-300 group-hover:translate-x-1 group-hover:text-fordje-blue" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold leading-tight">{industry.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{industry.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
